import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Boxes, ChevronRight, Coins, Columns3, Contact, Ellipsis, FilePlus2, FileStack, LogOut, PenTool, Receipt, Settings2, UserCircle2, Users, Wallet } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "New Quote", icon: FilePlus2, id: "nav-dashboard-link" },
  { to: "/quotes", label: "Quotes", icon: FileStack, id: "nav-quotes-link", tab: true },
  { to: "/design", label: "Design", icon: PenTool, id: "nav-design-link", tab: true },
  { to: "/customers", label: "Customers", icon: Contact, id: "nav-customers-link", tab: true },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, id: "nav-pipeline-link", tab: true },
  { to: "/invoices", label: "Invoices", icon: Receipt, id: "nav-invoices-link" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "nav-reports-link" },
  { to: "/products", label: "Products & pricing", icon: Boxes, id: "nav-products-link", owner: true },
  { to: "/settings", label: "Settings", icon: Settings2, id: "nav-settings-link", owner: true },
  { to: "/team", label: "Team", icon: Users, id: "nav-team-link", owner: true },
  { to: "/billing", label: "Billing", icon: Wallet, id: "nav-billing-link", owner: true },
];
const TAB_ORDER = ["/quotes", "/customers", "/design", "/pipeline"];

export const creditLabel = (n) => `${n} credit${n === 1 ? "" : "s"}`;

function CreditChip() {
  const { company, user } = useAuth();
  const n = company?.credits ?? 0;
  const cls = `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 font-mono-num text-[13px] font-semibold ${n > 0 ? "bg-[rgba(118,118,128,0.12)] text-black" : "bg-red-500/10 text-red-400"}`;
  const body = <><Coins className="h-3.5 w-3.5" /> <span data-testid="credit-balance-value">{creditLabel(n)}</span></>;
  if (user?.role !== "owner") {
    return <span data-testid="credit-balance-chip" title="Company credits: 1 per drawing read" className={cls}>{body}</span>;
  }
  return <Link to="/billing" data-testid="credit-balance-chip" title="Credits: 1 per drawing read" className={cls}>{body}</Link>;
}

const activeFor = (path) => {
  if (path === "/dashboard") return "/dashboard";
  if (path.startsWith("/quotes")) return "/quotes";
  if (path.startsWith("/customers") || path.startsWith("/projects")) return "/customers";
  for (const p of ["/pipeline", "/invoices", "/reports", "/design", "/billing", "/team", "/products", "/more"]) if (path.startsWith(p)) return p;
  return path.startsWith("/settings") ? "/settings" : null;
};

// Large title collapses into a 17pt title in the frosted top bar once the page's h1 scrolls away.
function useCollapsedTitle(path) {
  const [title, setTitle] = useState("");
  useEffect(() => {
    setTitle("");
    let obs;
    const t = setTimeout(() => {
      const h = document.querySelector("main h1");
      if (!h) return;
      obs = new IntersectionObserver(([e]) => setTitle(e.isIntersecting ? "" : h.textContent), { rootMargin: "-56px 0px 0px 0px" });
      obs.observe(h);
    }, 400);
    return () => { clearTimeout(t); obs?.disconnect(); };
  }, [path]);
  return title;
}

function Sidebar({ nav, active }) {
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[264px] flex-col border-r border-[rgba(60,60,67,0.18)] bg-[#F2F2F7] min-[1025px]:flex">
      <div className="flex h-16 items-center px-5"><Wordmark to="/dashboard" /></div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {nav.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} data-testid={id}
            className={() => `flex min-h-[40px] items-center gap-3 rounded-[10px] px-3 text-[15px] font-medium ${active === to ? "bg-[#0071E3] text-white" : "text-black hover:bg-black/5"}`}>
            <Icon className={`h-[18px] w-[18px] ${active === to ? "text-white" : "text-[#007AFF]"}`} />{label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function TabBar({ nav, active }) {
  const tabs = TAB_ORDER.map((p) => nav.find((n) => n.to === p)).filter(Boolean);
  const inMore = !tabs.some((t) => t.to === active) && active !== null;
  const item = (to, label, Icon, id, on) => (
    <Link key={to} to={to} data-testid={id} className={`flex min-h-[49px] flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${on ? "text-[#0062CC]" : "text-[#636366]"}`}>
      <Icon className="h-6 w-6" strokeWidth={on ? 2.2 : 1.8} />{label}
    </Link>
  );
  return (
    <nav data-testid="bottom-tab-bar" className="ios-bar safe-bottom fixed inset-x-0 bottom-0 z-50 flex border-t border-[rgba(60,60,67,0.29)] min-[1025px]:hidden">
      {tabs.map((t) => item(t.to, t.label, t.icon, `tab-${t.to.slice(1)}`, active === t.to))}
      {item("/more", "More", Ellipsis, "tab-more", inMore)}
    </nav>
  );
}

export function MorePage() {
  const { user } = useAuth();
  const nav = NAV.filter((n) => (!n.owner || user?.role === "owner") && !n.tab);
  return (
    <div className="space-y-6" data-testid="more-page">
      <h1 className="font-display text-4xl font-bold">More</h1>
      <div className="overflow-hidden rounded-xl bg-white">
        {nav.map(({ to, label, icon: Icon }, i) => (
          <Link key={to} to={to} data-testid={`more-link-${to.slice(1)}`} className={`flex min-h-[44px] items-center gap-3 px-4 text-[17px] text-black active:bg-black/5 ${i ? "border-t-[0.5px] border-[rgba(60,60,67,0.29)]" : ""}`}>
            <span className="flex h-7 w-7 items-center justify-center rounded-[7px] bg-[#0071E3] text-white"><Icon className="h-4 w-4" /></span>
            <span className="flex-1">{label}</span><ChevronRight className="h-4 w-4 text-[#C7C7CC]" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const active = activeFor(path);
  const nav = NAV.filter((n) => !n.owner || user?.role === "owner");
  const title = useCollapsedTitle(path);
  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <Sidebar nav={nav} active={active} />
      <div className="min-[1025px]:pl-[264px]">
        <header className="ios-bar sticky top-0 z-40 border-b border-[rgba(60,60,67,0.29)]">
          <div className="relative mx-auto flex h-14 max-w-[1640px] items-center justify-between gap-2 px-4 sm:px-6">
            <div className="flex shrink-0 items-center min-[1025px]:hidden"><Wordmark to="/dashboard" /></div>
            <p data-testid="topbar-title" className={`pointer-events-none absolute left-1/2 hidden max-w-[40%] -translate-x-1/2 truncate text-[17px] font-semibold text-black transition-opacity duration-200 sm:block ${title ? "opacity-100" : "opacity-0"}`}>{title}</p>
            <span className="hidden min-[1025px]:block" />
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <CreditChip />
              <Link to="/account" data-testid="header-account-link" title="My account" className="flex items-center gap-1.5 text-[15px] text-[#0062CC]">
                <UserCircle2 className="h-5 w-5" />
                <span data-testid="header-user-email" className="hidden xl:inline">{user?.email}</span>
              </Link>
              <button data-testid="logout-button" onClick={() => { signOut(); navigate("/login"); }} className="flex items-center gap-1.5 whitespace-nowrap px-1 text-[15px] text-[#0062CC]">
                <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Log out</span>
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-[1640px] px-4 pb-28 pt-6 sm:px-6 min-[1025px]:pb-12">
          <Outlet />
        </main>
      </div>
      <TabBar nav={nav} active={active} />
    </div>
  );
}
